import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { materialsApi, type MaterialCategory, type MaterialKind, type MaterialView } from "../../api/materials";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { Select } from "../../components/Select";

const KIND_OPTIONS: ("ALL" | MaterialKind)[] = ["ALL", "LOGO", "BROCHURE", "IMAGE", "VIDEO", "OTHER"];

export default function MaterialsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const locale = i18n.resolvedLanguage ?? "en";

  const [categories, setCategories] = useState<MaterialCategory[]>([]);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [items, setItems] = useState<MaterialView[]>([]);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"ALL" | MaterialKind>("ALL");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void materialsApi.categories(locale).then(setCategories).catch((e: ApiError) => setError(e.message));
  }, [locale]);

  useEffect(() => {
    void materialsApi
      .list(activeCategory ?? undefined)
      .then(setItems)
      .catch((e: ApiError) => setError(e.message));
  }, [activeCategory]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((m) => {
      if (kindFilter !== "ALL" && m.kind !== kindFilter) return false;
      if (!term) return true;
      const title = m.translations[locale]?.title ?? m.translations.en?.title ?? m.filename;
      return title.toLowerCase().includes(term) || m.filename.toLowerCase().includes(term);
    });
  }, [items, kindFilter, search, locale]);

  async function download(m: MaterialView) {
    try {
      const r = await materialsApi.presign(m.id);
      window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 24 }}>
      <aside style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <h2 style={asideHeading}>{t("materials.categories", "Categories")}</h2>
        <CategoryButton active={activeCategory === null} onClick={() => setActiveCategory(null)} label={t("materials.categoryAll", "All")} />
        {categories.map((c) => (
          <CategoryButton
            key={c.id}
            active={activeCategory === c.id}
            onClick={() => setActiveCategory(c.id)}
            label={c.name}
          />
        ))}
      </aside>

      <main style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-ink)" }}>{t("materials.title", "Materials")}</h1>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <input
            placeholder={t("materials.searchPlaceholder", "Search…")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...input, minWidth: 240 }}
          />
          <Select<"ALL" | MaterialKind>
            value={kindFilter}
            onChange={setKindFilter}
            options={KIND_OPTIONS.map((k) => ({
              value: k,
              label: k === "ALL" ? t("materials.kindAll", "All") : t(`materials.kind.${k}`, k),
            }))}
            style={{ minWidth: 160 }}
          />
        </div>

        {error && <div style={errorBox}>{error}</div>}

        {filtered.length === 0 ? (
          <p style={{ color: "var(--ink-3)" }}>{t("materials.noMatch", "No materials match.")}</p>
        ) : (
          <div style={grid}>
            {filtered.map((m) => {
              const tr = m.translations[locale] ?? m.translations.en;
              return (
                <div key={m.id} style={card}>
                  <div style={{ ...preview, background: previewBg(m.kind) }}>
                    <strong style={{ fontSize: 11, letterSpacing: 0.5, color: "var(--brand-ink)" }}>{t(`materials.kind.${m.kind}`, m.kind)}</strong>
                  </div>
                  <strong style={{ fontSize: 14, color: "var(--brand-ink)" }}>{tr?.title ?? m.filename}</strong>
                  {tr?.description && <p style={{ margin: 0, color: "var(--ink-3)", fontSize: 12 }}>{tr.description}</p>}
                  <small style={{ color: "var(--ink-4)" }}>{m.filename} · {t("materials.sizeKb", "{{size}} KB", { size: (m.sizeBytes / 1024).toFixed(0) })}</small>
                  <button type="button" onClick={() => download(m)} style={primary}>{t("materials.download", "Download")}</button>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function CategoryButton({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 12px",
        textAlign: "left",
        background: active ? "var(--brand-primary-50)" : "transparent",
        border: "none",
        borderRadius: 6,
        color: active ? "var(--brand-ink)" : "var(--ink)",
        fontWeight: active ? 600 : 400,
        cursor: "pointer",
        fontSize: 13,
      }}
    >
      {label}
    </button>
  );
}

function previewBg(kind: MaterialKind): string {
  switch (kind) {
    case "IMAGE":    return "linear-gradient(135deg, var(--info-bg), color-mix(in srgb, var(--info) 30%, var(--surface)))";
    case "BROCHURE": return "linear-gradient(135deg, var(--warn-bg), color-mix(in srgb, var(--warn) 30%, var(--surface)))";
    case "VIDEO":    return "linear-gradient(135deg, var(--tone-violet-bg), color-mix(in srgb, var(--tone-violet) 30%, var(--surface)))";
    case "LOGO":     return "linear-gradient(135deg, var(--ok-bg), color-mix(in srgb, var(--ok) 30%, var(--surface)))";
    default:         return "linear-gradient(135deg, var(--surface-2), var(--surface-muted))";
  }
}

const asideHeading: React.CSSProperties = { margin: "0 0 8px", fontSize: 12, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 };
const grid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 };
const card: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 6, padding: 12, background: "var(--surface)", borderRadius: 10, boxShadow: "var(--shadow-1)" };
const preview: React.CSSProperties = { aspectRatio: "4 / 3", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" };
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14 };
const primary: React.CSSProperties = { padding: "8px 12px", background: "var(--brand-primary-600)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", marginTop: 6 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
